import { ReactElement, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Theme
} from '@mui/material';
import { Icon, Typography } from '@zenput/legacy-ui-components';
import {
    DragDropContext,
    Draggable,
    DraggableLocation,
    DraggableStateSnapshot,
    DropResult,
    Droppable,
    DroppableProvided
} from 'react-beautiful-dnd';
import { makeStyles } from 'tss-react/mui';
import ActionButton from '../../shared/ActionButton';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import RichTextRenderer from '../../shared/RichTextRenderer';
import { ReorderingContext, useReorderingContext } from './ReorderingContext';
import { ELEVATION } from './constants';
import { Field, FieldTypes, Section, ServerSideField } from './types';
import { createReorderableStructure, getFieldIndexById, getReorderableFieldIndexById, reorder } from './utils';

const { Body } = Typography;

const useStyles = makeStyles()((theme: Theme) => {
    const { spacing, zenputColors, palette } = theme;

    return {
        page: {
            padding: spacing(2, 0),
            background: 'none'
        },
        field: {
            color: palette.grey[700],
            background: zenputColors.white,
            display: 'block',
            width: '100%'
        },
        section: {
            color: zenputColors.navGrey2,
            background: palette.secondary['100'],
            display: 'block',
            width: '100%'
        },
        title: {
            fontSize: 14
        },
        listItem: {
            padding: spacing(2, 1.5),
            minHeight: spacing(8)
        },
        truncateText: {
            '& .remirror-editor': {
                lineHeight: `${spacing(3)}px`,
                display: '-webkit-box',
                lineClamp: 2,
                '-webkit-line-clamp': 2,
                boxOrient: 'vertical',
                '-webkit-box-orient': 'vertical',
                overflow: 'hidden',
                wordWrap: 'break-word'
            }
        },
        fieldText: {
            paddingRight: spacing(0.5)
        },
        fields: {
            padding: spacing()
        },
        sectionList: {
            background: palette.secondary['100']
        },
        summary: {
            background: palette.secondary['100'],
            margin: 0,
            padding: 0,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        },
        summaryContent: {
            margin: 0
        },
        noPadding: {
            padding: 0
        },
        accordionSection: {
            display: 'block',
            width: '100%',
            background: palette.secondary['100'],
            boxSizing: 'border-box'
        },
        instructions: {
            textAlign: 'center',
            padding: spacing(1),
            background: zenputColors.white
        }
    };
});

interface FormFieldProps {
    field: Field;
    divider: boolean;
    icon?: ReactElement;
}

interface DraggableFieldProps {
    index: number;
    draggable?: boolean;
    field: Field;
    divider?: boolean;
    icon?: ReactElement;
}

interface FormFieldsProps {
    fields: Field[];
    droppableId: string;
}

interface SectionFieldProps {
    section: Section;
    droppableId: string;
}

interface SectionListProps {
    index: number;
    section: Section;
}

interface FormReorderingProps {
    sections: Section[];
    onChange: (data: unknown) => void;
}

interface FormReorderingPageProps {
    fields: ServerSideField[];
    onGoBack: (data: unknown) => void;
    onSave: (data: unknown) => void;
    _t: (translationKey: string) => string;
}

const FormField = (props: FormFieldProps) => {
    const { field, divider, icon } = props;
    const { classes } = useStyles();
    const context = useReorderingContext();

    return (
        <ListItem divider={divider} component="div" key={field.id} className={classes.listItem}>
            <ListItemIcon>
                <Body>{context.getFieldIndex(field.id)}</Body>
            </ListItemIcon>
            <ListItemText disableTypography className={classes.fieldText}>
                <RichTextRenderer className={classes.truncateText} content={field.title} />
            </ListItemText>
            <ListItemIcon>
                {icon}
                <Icon variant="df-drag" />
            </ListItemIcon>
        </ListItem>
    );
};

const DraggableField = (props: DraggableFieldProps) => {
    const { field, index, icon, draggable, divider = true } = props;
    const { classes } = useStyles();

    return (
        <Draggable key={field.id} draggableId={field.id.toString()} isDragDisabled={!draggable} index={index}>
            {(provided: DroppableProvided, snapshot: DraggableStateSnapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    elevation={snapshot.isDragging ? ELEVATION.DRAGGING : ELEVATION.IN_PLACE}
                    className={field.type === FieldTypes.SECTION ? classes.section : classes.field}
                >
                    <FormField field={field} divider={divider} icon={icon} />
                </div>
            )}
        </Draggable>
    );
};

const FormFields = (props: FormFieldsProps) => {
    const { fields, droppableId } = props;
    const { classes } = useStyles();

    return (
        <Droppable droppableId={droppableId} type="field">
            {(dropProvided: DroppableProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                    <List component="div" className={classes.noPadding}>
                        {fields.map((field, index) => (
                            <DraggableField draggable key={index} index={index} field={field} />
                        ))}
                        {dropProvided.placeholder}
                    </List>
                </div>
            )}
        </Droppable>
    );
};

const SectionField = (props: SectionFieldProps) => {
    const { section, droppableId } = props;
    const { classes } = useStyles();
    const getIndex = (index: number) => index + 1;
    const [expanded, setExpanded] = useState(false);
    const handleChange = () => setExpanded((state) => !state);

    return (
        <Droppable droppableId={droppableId} type="field">
            {(dropProvided: DroppableProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                    <Accordion square onChange={handleChange} expanded={expanded}>
                        <AccordionSummary classes={{ root: classes.summary, content: classes.summaryContent }}>
                            <DraggableField
                                index={0}
                                field={section.section}
                                divider={false}
                                icon={<Icon size={24} variant={expanded ? 'df-arrow-down' : 'df-arrow-up'} />}
                            />
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionSection}>
                            <List component="div">
                                {section.fields.map((field, index) => (
                                    <DraggableField
                                        draggable
                                        key={getIndex(index)}
                                        index={getIndex(index)}
                                        field={field}
                                    />
                                ))}
                            </List>
                            {dropProvided.placeholder}
                        </AccordionDetails>
                    </Accordion>
                </div>
            )}
        </Droppable>
    );
};

const SectionList = (props: SectionListProps) => {
    const { section, index: rootIndex } = props;
    const { id, section: sectionField, fields, isDragDisabled } = section;
    const { classes } = useStyles();

    return (
        <Draggable key={id} draggableId={id} index={rootIndex} isDragDisabled={isDragDisabled}>
            {(provided: DroppableProvided, snapshot: DraggableStateSnapshot) => (
                <div
                    // eslint-disable-next-line react/no-unknown-property
                    classes={{ root: classes.sectionList }}
                    elevation={snapshot.isDragging ? ELEVATION.DRAGGING : ELEVATION.IN_PLACE}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                >
                    {!sectionField ? (
                        <FormFields droppableId={id} fields={fields} />
                    ) : (
                        <SectionField droppableId={id} section={section} />
                    )}
                </div>
            )}
        </Draggable>
    );
};

const FormReordering = (props: FormReorderingProps) => {
    const { sections, onChange } = props;
    const { classes } = useStyles();
    const getFieldIndex = (fieldId: number) => getReorderableFieldIndexById(sections, fieldId) + 1;

    useEffect(() => {
        onChange(sections);
    }, [sections]);

    const handleSectionReorder = (destination: DraggableLocation, source: DraggableLocation) => {
        const newFieldOrder = reorder(sections, source.index, destination.index);
        onChange(newFieldOrder);
    };

    const handleFieldReorder = (destination: DraggableLocation, source: DraggableLocation) => {
        const sectionsCopy = Array.from(sections);
        const sourceSectionIndex = getFieldIndexById(sectionsCopy, source.droppableId);
        const destinationSectionIndex = getFieldIndexById(sectionsCopy, destination.droppableId);
        const sourceSection = sectionsCopy.find((section) => section.id === source.droppableId);
        const getReorderIndex = () => {
            const sourceIndex = source.index - (sourceSectionIndex ? 1 : 0);
            const destinationIndex = destination.index - (sourceSectionIndex ? 1 : 0);
            return [sourceIndex, destinationIndex];
        };

        if (sourceSectionIndex === destinationSectionIndex) {
            const [sourceIndex, destinationIndex] = getReorderIndex();
            const newFieldOrder = reorder(sourceSection.fields, sourceIndex, destinationIndex);
            sectionsCopy[sourceSectionIndex] = {
                ...sourceSection,
                fields: newFieldOrder
            };
            onChange(sectionsCopy);
        }
    };

    const onDragEnd = (result: DropResult) => {
        const { destination, source, type } = result;

        if (!destination || destination.index === undefined) {
            return;
        }
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        if (type === FieldTypes.SECTION && !sections[destination.index].isDragDisabled) {
            return handleSectionReorder(destination, source);
        }

        if (type === FieldTypes.FIELD) {
            return handleFieldReorder(destination, source);
        }
    };

    return (
        <ReorderingContext.Provider value={{ getFieldIndex }}>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="all-sections" type="section">
                    {(provided: DroppableProvided) => (
                        <div
                            className={classes.page}
                            elevation={ELEVATION.IN_PLACE}
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {sections.map((section, index) => {
                                return <SectionList key={section.id} section={section} index={index} />;
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </ReorderingContext.Provider>
    );
};

const FormReorderingPage = (props: FormReorderingPageProps) => {
    const { fields, onGoBack, onSave, _t } = props;
    const [initialSections, serializeReorderedSections] = createReorderableStructure(fields);
    const [sections, setSections] = useState<Section[]>(initialSections);
    const handleGoBack = () => onGoBack(serializeReorderedSections(sections));
    const handleSave = () => onSave(serializeReorderedSections(sections));
    const { classes } = useStyles();

    return (
        <PageLayout>
            {{
                appBar: <AppBar title={_t('mobile_mobileform_reorder_fields')} onGoBack={handleGoBack} />,
                content: (
                    <>
                        <div className={classes.instructions}>
                            <Body>{_t('mobile_forms_reordering_instructions')}</Body>
                        </div>
                        <FormReordering sections={sections} onChange={setSections} />
                    </>
                ),
                bottomBar: (
                    <BottomBar>
                        <ActionButton onClick={handleSave}>{_t('mobile_common_button_save')}</ActionButton>
                    </BottomBar>
                )
            }}
        </PageLayout>
    );
};

export default FormReorderingPage;
